import { PaymentStatus, StripePaymentFieldsFragment } from "@/gql/graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  CreditCardIcon,
} from "@heroicons/react/20/solid";
import { formatDate } from "@/lib/Formatters/formatDate";
import { ReceiptIcon } from "lucide-react";
import { useTranslations } from "use-intl";
import { PaymentListItem } from "@/app/Payments/GraphQL/paymentListQuery";
import { Badge } from "@/components/catalyst/badge";
import { paymentStatusColorMap } from "@/app/Payments/Utils/paymentColorMaps";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { RefundPaymentDialog } from "@/app/Bookings/Components/Dialogs/RefundPaymentDialog";
import { clsx } from "clsx";

interface PaymentItemProps {
  payment: PaymentListItem;
}

export function PaymentItem({ payment }: PaymentItemProps) {
  const t = useTranslations("payment");
  const { open: openRefundDialog } = useDialog(RefundPaymentDialog);

  const canRefund =
    payment.status === PaymentStatus.Paid ||
    payment.status === PaymentStatus.PartiallyRefunded;

  const stripePayment =
    payment.fulfilledBy?.__typename === "StripePayment"
      ? payment.fulfilledBy
      : null;

  let fulfilledBy = <NoFulfillment />;
  if (stripePayment) {
    fulfilledBy = <StripePaymentSection stripePayment={stripePayment} />;
  }

  return (
    <div className="group relative overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-950/5 transition-all duration-200 hover:shadow-md hover:ring-gray-950/10">
      <div className="relative">
        <dl className="flex flex-wrap">
          {/* Main content area */}
          <div className="relative w-full p-6">
            <div className="flex flex-col items-start justify-between">
              <dt className="text-sm font-medium text-gray-600">
                Payment Amount
              </dt>
              <dd className="mt-2 space-y-1">
                <p className="flex items-center gap-2">
                  <span
                    className={clsx("text-2xl font-bold text-gray-900", {
                      "line-through": payment.status === PaymentStatus.Refunded,
                    })}
                  >
                    {formatCurrency(payment.amount_paid, payment.currency, {
                      showZero: true,
                    })}
                  </span>

                  <span className="text-xs text-gray-500">
                    of {formatCurrency(payment.amount, payment.currency)} total
                  </span>
                </p>
                <p className="flex gap-2">
                  {payment.amount_refunded ? (
                    <Badge color="red">
                      (
                      {formatCurrency(
                        payment.amount_refunded,
                        payment.currency,
                      )}{" "}
                      refunded)
                    </Badge>
                  ) : null}

                  <Badge color={paymentStatusColorMap[payment.status]}>
                    {t(`status.${payment.status}`)}
                  </Badge>
                </p>
                <p className="text-xs text-gray-500">
                  {t(`statusDescription.${payment.status}`)}
                </p>
              </dd>
            </div>

            <div className="absolute right-6 top-6"></div>
          </div>

          {/* Fulfillment section */}
          <div className="w-full">{fulfilledBy}</div>

          {canRefund ? (
            <div className="flex w-full justify-end gap-2 border-t p-2">
              <Button
                outline
                onClick={() => {
                  openRefundDialog({
                    paymentId: payment.id,
                  });
                }}
              >
                <ArrowPathIcon className="h-4 w-4" />
                Refund
              </Button>
            </div>
          ) : null}
        </dl>
      </div>
    </div>
  );
}

function StripePaymentSection({
  stripePayment,
}: {
  stripePayment: StripePaymentFieldsFragment;
}) {
  return (
    <div className="border-t border-gray-100 bg-gray-50/50">
      {/* Payment details */}
      <div className="space-y-4 px-6 py-4">
        {/* Date */}
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
            <CalendarDaysIcon className="h-4 w-4 text-gray-600" />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Payment Date
            </p>
            <time
              dateTime={stripePayment.created_at}
              className="text-sm font-medium text-gray-900"
            >
              {formatDate(stripePayment.created_at)}
            </time>
          </div>
        </div>

        {/* Payment method */}
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
            <CreditCardIcon className="h-4 w-4 text-gray-600" />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Payment Method
            </p>
            <p className="text-sm font-medium capitalize text-gray-900">
              {stripePayment.brand} Card
            </p>
          </div>
        </div>

        {/* Transaction ID */}
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
            <ReceiptIcon className="h-4 w-4 text-gray-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
              Transaction ID
            </p>
            <a
              href={`https://dashboard.stripe.com/payments/${stripePayment.payment_intent_id}`}
              target="_blank"
              rel="noreferrer"
              className="group/link inline-flex items-center gap-2 font-mono text-sm text-gray-700 transition-colors duration-150 hover:text-indigo-600 hover:underline"
            >
              <span className="max-w-[200px] truncate">
                {stripePayment.payment_intent_id}
              </span>
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 opacity-60 transition-opacity duration-150 group-hover/link:opacity-100" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function NoFulfillment() {
  return (
    <div className="border-t border-gray-100 bg-gray-50/30">
      <div className="px-6 py-8 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <CreditCardIcon className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-500">
          No payment method on file
        </p>
        <p className="mt-1 text-xs text-gray-400">
          Payment details will appear here when processed
        </p>
      </div>
    </div>
  );
}
